/**
 * BaseView - Abstract base class for all view components
 *
 * Provides lifecycle hooks and utility methods for view rendering
 */

import type { RouteParams } from '@/types';

/**
 * Abstract base view class
 * All page views should extend this class
 */
export abstract class BaseView {
	/**
	 * Route parameters extracted from URL
	 */
	protected params: RouteParams = {};

	/**
	 * Query string parameters
	 */
	protected query: URLSearchParams = new URLSearchParams();

	/**
	 * Container element where view is mounted
	 */
	protected container: HTMLElement | null = null;

	/**
	 * Event listeners registered by this view
	 * Stored for cleanup on unmount
	 */
	private eventListeners: Array<{
		element: Element | Window | Document;
		event: string;
		handler: EventListener;
		options?: AddEventListenerOptions;
	}> = [];

	/**
	 * Constructor
	 */
	constructor() {
		// Override in subclasses if needed
	}

	/**
	 * Render method - returns HTML string for this view
	 * Must be implemented by subclasses
	 */
	abstract render(): string;

	/**
	 * Set route parameters
	 */
	setParams(params: RouteParams): void {
		this.params = params;
	}

	/**
	 * Set query parameters
	 */
	setQuery(query: URLSearchParams): void {
		this.query = query;
	}

	/**
	 * Mount the view to a container element
	 */
	async mount(container: HTMLElement): Promise<void> {
		this.container = container;

		// Render HTML
		const html = this.render();
		container.innerHTML = html;

		// Call lifecycle hook
		await this.onMount();
	}

	/**
	 * Unmount the view and cleanup
	 */
	async unmount(): Promise<void> {
		// Call lifecycle hook first
		await this.onUnmount();

		// Remove all event listeners
		this.removeAllEventListeners();

		// Clear container
		if (this.container) {
			this.container.innerHTML = '';
			this.container = null;
		}
	}

	/**
	 * Lifecycle hook - called after view is mounted to DOM
	 * Override in subclasses to add event listeners, initialize components, etc.
	 */
	protected async onMount(): Promise<void> {
		// Override in subclasses
	}

	/**
	 * Lifecycle hook - called before view is unmounted
	 * Override in subclasses to cleanup
	 */
	protected async onUnmount(): Promise<void> {
		// Override in subclasses
	}

	/**
	 * Lifecycle hook - called when route params/query change
	 * Override in subclasses to handle updates
	 */
	protected async onUpdate(): Promise<void> {
		// Override in subclasses
	}

	/**
	 * Add an event listener and track it for cleanup
	 */
	protected addEventListener<K extends keyof HTMLElementEventMap>(
		element: HTMLElement | Window | Document,
		event: K,
		handler: (e: HTMLElementEventMap[K]) => void,
		options?: AddEventListenerOptions
	): void {
		const listener = handler as EventListener;
		element.addEventListener(event, listener, options);
		this.eventListeners.push({ element, event, handler: listener, options });
	}

	/**
	 * Add a generic event listener and track it
	 */
	protected addGenericEventListener(
		element: Element | Window | Document,
		event: string,
		handler: EventListener,
		options?: AddEventListenerOptions
	): void {
		element.addEventListener(event, handler, options);
		this.eventListeners.push({ element, event, handler, options });
	}

	/**
	 * Remove all tracked event listeners
	 */
	private removeAllEventListeners(): void {
		for (const { element, event, handler, options } of this.eventListeners) {
			element.removeEventListener(event, handler, options);
		}
		this.eventListeners = [];
	}

	/**
	 * Query selector within view's container
	 */
	protected querySelector<T extends Element = Element>(selector: string): T | null {
		if (!this.container) return null;
		return this.container.querySelector<T>(selector);
	}

	/**
	 * Query selector all within view's container
	 */
	protected querySelectorAll<T extends Element = Element>(selector: string): T[] {
		if (!this.container) return [];
		return Array.from(this.container.querySelectorAll<T>(selector));
	}

	/**
	 * Get a route parameter value
	 */
	protected getParam(key: string): string | undefined {
		return this.params[key];
	}

	/**
	 * Get a query parameter value
	 */
	protected getQueryParam(key: string): string | null {
		return this.query.get(key);
	}

	/**
	 * Get all query parameter values for a key
	 */
	protected getQueryParams(key: string): string[] {
		return this.query.getAll(key);
	}

	/**
	 * Check if query parameter exists
	 */
	protected hasQueryParam(key: string): boolean {
		return this.query.has(key);
	}

	/**
	 * Emit a custom event from the view
	 */
	protected emit(eventName: string, detail?: unknown): void {
		if (!this.container) return;

		const event = new CustomEvent(eventName, {
			detail,
			bubbles: true,
			cancelable: true,
		});

		this.container.dispatchEvent(event);
	}

	/**
	 * Update the view with new data
	 * Re-renders and re-mounts
	 */
	async update(): Promise<void> {
		if (!this.container) return;

		// Call update hook
		await this.onUpdate();

		// Re-render
		const html = this.render();
		this.container.innerHTML = html;

		// Re-mount (calls onMount again)
		await this.onMount();
	}
}
